import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useDebounce } from "../../../../hooks/useDebounce";
import Select from "../../../form/select/Select";
import Form from "../../../form/Form";
import useForm from "../../../../hooks/useForm";
import Input from "../../../form/Input";
import Textarea from "../../../form/Textarea";
import {
  taxonomyAddEdit,
  taxonomyDetails,
  taxonomyList,
} from "../../../../services/taxonomyService";
import Button from "../../../form/Button";
import SelectSingleImage from "../../../form/SelectSingleImage";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import LanguageTabs from "../../../elements/LanguageSwitcher";
import {
  supportedLanguages,
  generateSlug,
  userLang,
} from "../../../../helpers";

const CategoryManage = ({
  open,
  close = () => {},
  getResponse = () => {},
  data = {},
  type,
}) => {
  const [selected, setSelected] = useState("main");
  const [categoryImage, setCategoryImage] = useState([]);
  const [currentLang, setCurrentLang] = useState(userLang || "en");

  const handleLangChange = (lang) => {
    setCurrentLang(lang);
  };

  const validation = {
    parent: {
      required: selected === "sub" ? true : false,
      message: "Parent category is required",
    },
    title: { required: true, message: "Title is required" },
  };

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: { status: "active" },
  });

  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const {
    data: taxonomyCategoryList,
    loading,
    updateData,
    isFetching,
  } = useQuery({
    queryKey: ["taxonomyForCategories", filters, search, type],
    queryFn: async () => {
      const res = await taxonomyList({
        ...(search && { keyword: search }),
        type: type,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const {
    data: details,
    isLoading: detailsLoading,
    isFetching: detailsFetching,
  } = useQuery({
    queryKey: ["category-details", data?._id],
    queryFn: async () => {
      const res = await taxonomyDetails({ id: data?._id });
      return res?.data;
    },
    enabled: !!data?._id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        ...details,
        content: {
          ...supportedLanguages.reduce((acc, lang) => {
            acc[lang.code] = {
              title: details?.content?.[lang.code]?.title || "",
              subTitle: details?.content?.[lang.code]?.subTitle || "",
              content: details?.content?.[lang.code]?.content || "",
              seo: details?.content?.[lang.code]?.seo || {},
            };
            return acc;
          }, {}),
        },
        title: details?.content?.[currentLang]?.title || "",
        parent: details?.parent,
      });
      setSelected(details?.hierarchyType);
      setCategoryImage(details?.mediaGallery?.[0] || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const {
    mutate: onSubmit,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        type: type,
        slug: `${generateSlug(val?.content?.[userLang]?.title.replace(/\s+/g, "-").toLowerCase(), Date.now())}`,
        lang: userLang,
        content: val?.content,
        hierarchyType: selected,
        status: val?.status,
        mediaGallery:
          categoryImage && categoryImage.length !== 0
            ? [categoryImage?._id]
            : [],
        ...(selected === "sub" && { parent: val?.parent }),
      };
      return await taxonomyAddEdit(
        details?._id
          ? {
              ...(details?._id && {
                id: details?._id,
                ...payload,
              }),
            }
          : {
              ...payload,
            },
      );
    },
    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        resetField();
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <div className="relative px-3 py-3">
      <Form onSubmit={onSubmit} handleSubmit={handleSubmit} loading={loading}>
        <div className="flex gap-6 max-w-5xl mx-auto px-6 py-6">
          <div className="w-[80%] rounded-xl">
            <h2 className="text-xs font-medium text-text-disabled mb-1 font-CanelaDeck">
              Select Category Type
            </h2>

            <div className="flex gap-4">
              {/* Parent Category */}
              <label
                className={`flex items-center flex-1 p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                  selected === "parent"
                    ? "border-primary bg-primary/10"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="categoryType"
                  value="parent"
                  checked={selected === "main"}
                  onChange={() => setSelected("main")}
                  className="form-radio text-primary w-5 h-5 mr-3 ring-0 focus:ring-0 focus:outline-none"
                />
                <span className="text-gray-800 font-medium">
                  Parent Category
                </span>
              </label>

              {/* Sub Category */}
              <label
                className={`flex items-center flex-1 p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                  selected === "sub"
                    ? "border-primary bg-primary/10"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="categoryType"
                  value="sub"
                  checked={selected === "sub"}
                  onChange={() => setSelected("sub")}
                  className="form-radio text-primary w-5 h-5 mr-3 ring-0 focus:ring-0 focus:outline-none"
                />
                <span className="text-gray-800 font-medium">Sub Category</span>
              </label>
            </div>
          </div>
          {/* <div className="border border-gray-300 rounded-lg p-4 space-y-2">
            <SelectSingleImage
              label="Upload Image"
              size="md"
              value={categoryImage}
              onChange={(img) => setCategoryImage(img)}
              onRemove={() => setCategoryImage(null)}
            />
          </div> */}
        </div>

        {selected === "sub" && (
          <div className="grid grid-cols-1 gap-3">
            <FlexibleSelect
              label={"Parent category"}
              isSearch={false}
              onSearchHandler={"setCountryKeyword"}
              placeholder={"Select a category"}
              onChange={(e) =>
                handleChange({ target: { name: "parent", value: e } })
              }
              selectedValue={values?.parent}
              searchValue={keyword}
              dropdownData={taxonomyCategoryList?.data
                ?.filter((item) => details?._id !== item?._id)
                .map((cn) => {
                  return {
                    name: cn?.content?.[userLang]?.title,
                    value: cn?._id,
                  };
                })}
              {...formValidate(errors, "parent")}
            />
          </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          {/* <LanguageTabs
            languages={supportedLanguages}
            value={currentLang}
            onChange={handleLangChange}
          /> */}
          <Input
            label={"Title"}
            inputType={"title"}
            inputPlaceholder="Enter title"
            inputName={`content.${currentLang}.title`}
            value={values?.content?.[currentLang]?.title || ""}
            onChange={(e) => {
              const newValue = e.target.value;
              setFieldsValue({
                ...values,
                title: newValue,
                content: {
                  ...values.content,
                  [currentLang]: {
                    ...values.content?.[currentLang],
                    title: newValue,
                  },
                },
              });
              formValidate(errors, `content.${currentLang}.title`, newValue);
            }}
            {...formValidate(errors, "title")}
          />
        </div>
        <div className="grid grid-cols-1 gap-3">
          <FlexibleSelect
            label={"Status"}
            placeholder={"Choose status"}
            onChange={(e) =>
              handleChange({ target: { name: "status", value: e } })
            }
            selectedValue={values?.status}
            dropdownData={[
              { name: "Active", value: "active" },
              { name: "Inactive", value: "inactive" },
            ]}
            {...formValidate(errors, "status")}
          />
        </div>
        <div className="pt-3 pb-6 flex justify-end">
          <Button
            buttonType="submit"
            buttonClasses={"min-w-36"}
            buttonLabel={details?._id ? "Update" : "Save"}
            {...((isLoading || loading || detailsLoading) && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
        </div>
      </Form>
    </div>
  );
};

export default CategoryManage;
